/** @format */

import { useState, useEffect } from '@wordpress/element';

const AdminEntriesTable = ({ ageStandard = 'standard_hs' }) => {
	const [meetings, setMeetings] = useState([]);
	const [selectedMeetingPostId, setSelectedMeetingPostId] = useState('');
	const [meetingDetails, setMeetingDetails] = useState(null);
	const [entries, setEntries] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	// Bulk selection state tracking
	const [selectedEntryIds, setSelectedEntryIds] = useState([]);
	const [bulkAction, setBulkAction] = useState('-1');

	// Filter matrices state metrics
	const [filterGender, setFilterGender] = useState('ALL');
	const [filterClass, setFilterClass] = useState('ALL');
	const [filterEvent, setFilterEvent] = useState('ALL');

	// Add these to track which relay sub-rows are expanded by ID
	const [expandedRelays, setExpandedRelays] = useState([]);

	// Add the toggler handler to manage additions/removals from the array
	const toggleRelayExpand = (rowId) => {
		setExpandedRelays((prevExpanded) => (prevExpanded.includes(rowId) ? prevExpanded.filter((id) => id !== rowId) : [...prevExpanded, rowId]));
	};

	// 1. Initial mounting: Fetch available track meets and read deep-linked bookmarks
	useEffect(() => {
		fetch(`${ammData.api_url}/admin/meetings`, {
			headers: { 'X-WP-Nonce': ammData.nonce },
		})
			.then((res) => res.json())
			.then((data) => {
				if (Array.isArray(data) && data.length > 0) {
					setMeetings(data);

					const urlParams = new URLSearchParams(window.location.search);
					const bookmarkedId = urlParams.get('meetingPostId');

					if (bookmarkedId && data.some((m) => m.id === parseInt(bookmarkedId))) {
						setSelectedMeetingPostId(parseInt(bookmarkedId));
					} else {
						setSelectedMeetingPostId(data[0].id);
						updateUrlParam('meetingPostId', data[0].id);
					}
				}
			})
			.catch((err) => console.error('Error loading administrative meetings:', err));
	}, []);

	const updateUrlParam = (key, value) => {
		const url = new URL(window.location.href);
		url.searchParams.set(key, value);
		window.history.pushState({}, '', url.toString());
	};

	// 2. Clear selections and fetch row collections when selected meet shifts
	useEffect(() => {
		if (!selectedMeetingPostId) return;

		setLoading(true);
		setSelectedEntryIds([]); // Flush checkbox selections
		updateUrlParam('meetingPostId', selectedMeetingPostId);

		fetch(`${ammData.api_url}/meetings/${selectedMeetingPostId}`)
			.then((res) => res.json())
			.then((details) => setMeetingDetails(details))
			.catch((err) => console.error('Error retrieving meeting rules details:', err));

		fetch(`${ammData.api_url}/admin/entries?meetingPostId=${selectedMeetingPostId}`, {
			headers: { 'X-WP-Nonce': ammData.nonce },
		})
			.then((res) => res.json())
			.then((entryRows) => {
				setEntries(Array.isArray(entryRows) ? entryRows : []);
				setLoading(false);
			})
			.catch((err) => {
				console.error('Entry engine data query fault:', err);
				setLoading(false);
			});
	}, [selectedMeetingPostId]);

	// Single Row Status Operations
	const handleStatusUpdate = (entryId, targetStatus) => {
		fetch(`${ammData.api_url}/admin/entries/update-status`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': ammData.nonce,
			},
			body: JSON.stringify({ entryId: entryId, status: targetStatus }),
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.success) {
					setEntries((prev) => (targetStatus === 'REMOVE_PERMANENTLY' ? prev.filter((item) => item.id !== entryId) : prev.map((item) => (item.id === entryId ? { ...item, status: targetStatus } : item))));
					setSelectedEntryIds((prev) => prev.filter((id) => id !== entryId));
				}
			})
			.catch((err) => console.error('Error executing status update:', err));
	};

	// Batch Action Processing
	const handleBulkActionApply = (e) => {
		e.preventDefault();
		if (bulkAction === '-1' || selectedEntryIds.length === 0) return;

		if (bulkAction === 'REMOVE_PERMANENTLY') {
			if (!confirm(`Are you absolutely sure you want to permanently delete these ${selectedEntryIds.length} selected entries?`)) {
				return;
			}
		}

		setLoading(true);

		fetch(`${ammData.api_url}/admin/entries/bulk-update`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': ammData.nonce,
			},
			body: JSON.stringify({
				entryIds: selectedEntryIds,
				status: bulkAction,
			}),
		})
			.then((res) => {
				if (!res.ok) {
					throw new Error(`Server returned status code: ${res.status}`);
				}
				return res.json();
			})
			.then((data) => {
				if (data && data.success) {
					setEntries((prev) => {
						if (bulkAction === 'REMOVE_PERMANENTLY') {
							return prev.filter((item) => !selectedEntryIds.includes(item.id));
						}
						return prev.map((item) => (selectedEntryIds.includes(item.id) ? { ...item, status: bulkAction } : item));
					});
					setSelectedEntryIds([]);
					setBulkAction('-1');
				} else {
					alert('Database update rejected: Please verify user authorization parameters.');
				}
				setLoading(false);
			})
			.catch((err) => {
				console.error('Bulk action synchronization failure:', err);
				alert('Network connection error. Database modifications were rolled back.');
				setLoading(false);
			});
	};

	// Checkbox interaction handlers
	const handleSelectAllToggle = (e) => {
		if (e.target.checked) {
			const visibleIds = filteredEntries.map((item) => item.id);
			setSelectedEntryIds(visibleIds);
		} else {
			setSelectedEntryIds([]);
		}
	};

	const handleRowCheckboxToggle = (entryId) => {
		setSelectedEntryIds((prev) => (prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]));
	};

	// Extract layout filtration options safely handling structural variance
	const eventOptions = Array.isArray(meetingDetails?.events) ? [...new Set(meetingDetails.events.map((e) => e.disciplineName || e.eventName || e.event))] : [];

	const classOptions = Array.isArray(meetingDetails?.events) ? [...new Set(meetingDetails.events.map((e) => e.ageClass || e.class))] : [];

	// Table view matching criteria
	const filteredEntries = entries.filter((item) => {
		const namePart = item.isRelay || item.relayName ? item.relayName || '' : `${item.firstName || ''} ${item.lastName || ''}`;

		const matchesSearch = `${namePart} ${item.clubName || ''}`.toLowerCase().includes(searchQuery.toLowerCase());
		const matchesGender = filterGender === 'ALL' || item.sexCode === filterGender;
		const matchesClass = filterClass === 'ALL' || (item.eventClass || item.ageClass) === filterClass;
		const matchesEvent = filterEvent === 'ALL' || item.eventName === filterEvent;

		return matchesSearch && matchesGender && matchesClass && matchesEvent;
	});

	const isAllVisibleSelected = filteredEntries.length > 0 && filteredEntries.every((item) => selectedEntryIds.includes(item.id));

	// Dynamic ruleset normalization
	const activeRuleset = meetingDetails?.ageStandards || ageStandard;
	const ageRulesClass = activeRuleset === 'standard_intl' ? 'rules-international' : 'rules-hs';

	const getCountText = () => {
		const count = filteredEntries.length;
		return `${count} ${count === 1 ? 'entry' : 'entries'}`;
	};

	return (
		<div className={`amm-admin-native-dashboard ${ageRulesClass}`}>
			{/* Row 1: Meet Dropdown, Real-time Rule Badge, and Search Bar */}
			<div className="tablenav top d-flex-between" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0' }}>
				<div className="alignleft actions" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
					<label htmlFor="amm-meet-select" className="screen-reader-text">
						Select Track Meet
					</label>
					<select id="amm-meet-select" style={{ maxWidth: '100%' }} value={selectedMeetingPostId} onChange={(e) => setSelectedMeetingPostId(parseInt(e.target.value))}>
						<option value="">-- Select Active Competition Schedule --</option>
						{meetings.map((m) => (
							<option key={m.id} value={m.id}>
								{m.title}
							</option>
						))}
					</select>

					<span className={`amm-rules-badge standard-${activeRuleset}`} style={{ fontStyle: 'italic', display: 'flex', gap: '5px', alignItems: 'center' }}>
						Active Context: <strong>{activeRuleset === 'standard_intl' ? 'World Athletics (International)' : 'ISSA Jamaican HS'}</strong>
					</span>
				</div>

				<div className="search-box">
					<label className="screen-reader-text" htmlFor="entry-search-input">
						Search Entries:
					</label>
					<input type="search" id="entry-search-input" placeholder="Search entries, relays, or clubs..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ margin: 0 }} />
				</div>
			</div>

			{/* Row 2: Bulk Processing Options, Filter Matrices */}
			<div className="tablenav top d-flex-between" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0' }}>
				<form className="alignleft actions bulkactions" onSubmit={handleBulkActionApply} style={{ margin: 0, display: 'flex', gap: '6px' }}>
					<label htmlFor="bulk-action-selector-top" className="screen-reader-text">
						Select bulk action
					</label>
					<select id="bulk-action-selector-top" value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
						<option value="-1">Bulk actions</option>
						<option value="VERIFIED">Verify Entry</option>
						<option value="SCRATCHED">Scratch From Event</option>
						<option value="OK">Reset to Pending</option>
						<option value="REMOVE_PERMANENTLY">Delete Record</option>
					</select>
					<input type="submit" id="doaction" className="button action" value="Apply" disabled={selectedEntryIds.length === 0 || bulkAction === '-1'} />
				</form>

				<div className="alignright actions" style={{ paddingRight: '0' }}>
					<select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)}>
						<option value="ALL">All Discipline Events</option>
						{eventOptions.map((opt, index) => (
							<option key={`${opt}-${index}`} value={opt}>
								{opt}
							</option>
						))}
					</select>

					<select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
						<option value="ALL">All Division Classes</option>
						{classOptions.map((opt, index) => (
							<option key={`${opt}-${index}`} value={opt}>
								{opt}
							</option>
						))}
					</select>

					<select value={filterGender} onChange={(e) => setFilterGender(e.target.value)}>
						<option value="ALL">All Genders</option>
						<option value="M">Male (M)</option>
						<option value="F">Female (F)</option>
					</select>
				</div>
			</div>

			{/* Row 3: Counters */}
			<div className="tablenav-pages" style={{ textAlign: 'right', margin: '5px 0 12px 0', color: '#646970' }}>
				<span className="displaying-num" style={{ fontSize: '13px' }}>
					{getCountText()}
				</span>
				{selectedEntryIds.length > 0 && <span style={{ marginLeft: '8px', color: '#2271b1', fontWeight: '600', fontSize: '13px' }}>({selectedEntryIds.length} selected)</span>}
			</div>

			{/* Native WP Table View Grid */}
			{loading ? (
				<div className="amm-loading-container p-3 border rounded bg-white">
					<span className="spinner is-active mb-0 align-middle"></span>
					<span className="ms-2 text-secondary">Processing compilation registries...</span>
				</div>
			) : filteredEntries.length === 0 ? (
				<div className="notice notice-warning inline">
					<p>No verified competitor or relay records matched the active matrix filters.</p>
				</div>
			) : (
				<table className="wp-list-table widefat fixed striped table-view-list entries-table">
					<thead>
						<tr>
							<td id="cb" className="manage-column column-cb check-column">
								<label className="screen-reader-text" htmlFor="cb-select-all-1">
									Select All
								</label>
								<input id="cb-select-all-1" type="checkbox" checked={isAllVisibleSelected} onChange={handleSelectAllToggle} />
							</td>
							<th scope="col" className="manage-column column-primary" style={{ width: '30%', fontWeight: '600' }}>
								Competitor / Squad Pool
							</th>
							<th scope="col" className="manage-column" style={{ width: '10%', fontWeight: '600', textAlign: 'center' }}>
								Gender
							</th>
							<th scope="col" className="manage-column" style={{ width: '22%', fontWeight: '600', textAlign: 'center' }}>
								Affiliation
							</th>
							<th scope="col" className="manage-column" style={{ width: '23%', fontWeight: '600' }}>
								Event / Classification
							</th>
							<th scope="col" className="manage-column" style={{ width: '15%', fontWeight: '600', textAlign: 'center' }}>
								Validation Status
							</th>
						</tr>
					</thead>

					<tbody id="the-list">
						{filteredEntries.map((row) => {
							const isRowChecked = selectedEntryIds.includes(row.id);
							const isRelay = row.entryType === 'relay';
							const isExpanded = expandedRelays.includes(row.id);

							const statusCleanClass = `status-${(row.status || 'PENDING').toLowerCase()}`;
							const genderCleanClass = `gender-${(row.sexCode || 'M').toLowerCase()}`;
							const eventCleanClass = `event-${(row.eventName || row.disciplineName || '').toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
							const classCleanClass = `class-${(row.eventClass || row.ageClass || '').toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

							const displayName = isRelay ? (
								<span className="text-primary text-uppercase fw-bold d-flex align-items-center" style={{ cursor: 'pointer', gap: '4px' }} onClick={() => toggleRelayExpand(row.id)}>
									<span className={`dashicons ${isExpanded ? 'dashicons-arrow-down-alt2 text-secondary' : 'dashicons-arrow-right-alt2 text-muted'}`} style={{ fontSize: '14px', width: '14px', height: '14px' }}></span>
									<span className="dashicons dashicons-groups text-dark" style={{ fontSize: '17px', width: '17px', height: '17px' }}></span>
									{row.relayName || `${row.clubName || ''} Relay Squad`}
								</span>
							) : (
								<span>
									{(row.lastName || '').toUpperCase()}, {row.firstName}
									{row.middleName ? ` ${row.middleName.charAt(0).toUpperCase()}.` : ''}
								</span>
							);

							// 🟢 FIX: Wrap in a proper key-bound React Fragment instead of an inner wrapping <tr>
							return (
								<React.Fragment key={`entry-group-${row.entryType}-${row.id}`}>
									{/* Sibling Row 1: Main Admin Entry Data Row */}
									<tr className={`format-standard ${isRowChecked ? 'selected' : ''} ${isRelay ? 'relay-parent-row' : ''}`}>
										<th scope="row" className="check-column">
											<label className="screen-reader-text" htmlFor={`cb-select-${row.id}`}>
												Select entry row record
											</label>
											<input id={`cb-select-${row.id}`} type="checkbox" checked={isRowChecked} onChange={() => handleRowCheckboxToggle(row.id)} />
										</th>

										<td className="username column-username column-primary has-row-actions">
											<strong>{displayName}</strong>

											<div className="row-actions">
												<span className="approve">
													<button type="button" className="button-link text-success" onClick={() => handleStatusUpdate(row.id, 'VERIFIED')}>
														Verify
													</button>
													&nbsp;|&nbsp;
												</span>
												<span className="unapprove">
													<button type="button" className="button-link text-warning" onClick={() => handleStatusUpdate(row.id, 'SCRATCHED')}>
														Scratch
													</button>
													&nbsp;|&nbsp;
												</span>
												<span className="reset">
													<button type="button" className="button-link text-secondary" onClick={() => handleStatusUpdate(row.id, 'OK')}>
														Reset
													</button>
													&nbsp;|&nbsp;
												</span>
												<span className="trash">
													<button
														type="button"
														className="button-link delete"
														onClick={() => {
															const confirmMsg = isRelay ? `Permanently dissolve the relay roster team entries for "${row.relayName || 'this squad'}"?` : `Permanently drop event entry record for ${row.firstName} ${row.lastName}?`;
															if (confirm(confirmMsg)) {
																handleStatusUpdate(row.id, 'REMOVE_PERMANENTLY');
															}
														}}
														style={{ color: '#b32d2e' }}>
														Delete Permanent
													</button>
												</span>
											</div>
										</td>

										<td className={genderCleanClass} style={{ textAlign: 'center' }}>
											<span className="badge border px-2 py-0.5 rounded-pill bg-light font-monospace">{row.sexCode || 'M'}</span>
										</td>

										<td style={{ textAlign: 'center' }}>
											<span className="fw-medium">{row.clubName || row.clubAffiliation || '—'}</span>
											{row.country && <span className="text-muted small"> [{row.country}]</span>}
										</td>

										<td className={`${eventCleanClass} ${classCleanClass}`}>
											<div className="event-classification" style={{ gap: '4px', display: 'flex', alignItems: 'center' }}>
												<span className="amm-event-name fw-semibold text-dark">{row.eventName || row.disciplineName || row.disciplineId}</span>
												{(row.eventClass || row.ageClass) && (
													<span className="amm-event-class badge bg-secondary ms-1 px-1.5 text-uppercase text-white" style={{ fontSize: '10px' }}>
														{row.eventClass || row.ageClass}
													</span>
												)}
											</div>
										</td>

										<td className={`${statusCleanClass} fw-bold`} style={{ textAlign: 'center' }}>
											<span className="status-label">{row.status || 'PENDING'}</span>
										</td>
									</tr>

									{/* Sibling Row 2: Collapsible Roster Panel Sub-Row */}
									{isRelay && isExpanded && (
										<tr className="amm-relay-roster-row" style={{ backgroundColor: '#f6f7f7' }}>
											{/* Empty cell matching the check column to snap content to grid */}
											<td></td>
											<td colSpan="5" style={{ padding: '12px 15px 15px 15px' }}>
												<div className="card shadow-sm border-start border-primary border-3 bg-white p-3 rounded" style={{ borderLeft: '4px solid #2271b1', background: '#fff', padding: '15px', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
													<h5 style={{ fontSize: '13px', fontWeight: '600', margin: '0 0 10px 0', color: '#1d2327', display: 'flex', alignItems: 'center', gap: '5px' }}>
														<span className="dashicons dashicons-editor-ol" style={{ fontSize: '16px', width: '16px', height: '16px' }}></span>
														Squad Running Order Roster Pool
													</h5>

													{Array.isArray(row.members) && row.members.length > 0 ? (
														<div style={{ gap: '10px', display: 'flex', flexWrap: 'wrap' }}>
															{row.members.map((member, idx) => (
																<div key={idx} className="badge bg-light text-dark border p-2 rounded" style={{ fontSize: '12px', minWidth: '180px', background: '#f0f0f1', border: '1px solid #dcdcde', padding: '6px 10px', borderRadius: '3px', display: 'flex', alignItems: 'center' }}>
																	<span className="text-primary fw-bold" style={{ color: '#2271b1', marginRight: '6px' }}>
																		{Number(member.isAlternate) === 1 ? `Alt:` : `Leg ${member.runningOrder || idx + 1}:`}
																	</span>
																	<span style={{ fontWeight: '500' }}>{member.lastName ? `${member.lastName.toUpperCase()}, ${member.firstName}` : member.firstName}</span>
																	<span className="text-muted small" style={{ marginLeft: '4px', color: '#646970' }}>
																		({member.sexCode || 'M'})
																	</span>
																</div>
															))}
														</div>
													) : (
														<p className="text-muted small mb-0" style={{ fontStyle: 'italic', margin: 0, color: '#646970', fontSize: '12px' }}>
															<span className="dashicons dashicons-info" style={{ fontSize: '14px', width: '14px', height: '14px', verticalAlign: 'text-bottom', marginRight: '3px' }}></span>
															No running legs or substitute pool athletes assigned to this squad yet.
														</p>
													)}
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>

					<tfoot>
						<tr>
							<td className="check-column">
								<input id="cb-select-all-2" type="checkbox" checked={isAllVisibleSelected} onChange={handleSelectAllToggle} />
							</td>
							<th scope="col" className="manage-column column-primary" style={{ fontWeight: '600' }}>
								Competitor / Squad Pool
							</th>
							<th scope="col" className="manage-column" style={{ fontWeight: '600', textAlign: 'center' }}>
								Gender
							</th>
							<th scope="col" className="manage-column" style={{ fontWeight: '600', textAlign: 'center' }}>
								Affiliation
							</th>
							<th scope="col" className="manage-column" style={{ fontWeight: '600' }}>
								Event / Classification
							</th>
							<th scope="col" className="manage-column" style={{ fontWeight: '600', textAlign: 'center' }}>
								Validation Status
							</th>
						</tr>
					</tfoot>
				</table>
			)}
		</div>
	);
};

export default AdminEntriesTable;
